// src/utils/dashboardUtils.js - Updated version without date functions
import { CUSTOMER_CODE_PATTERN } from '../constants/businessLogic';

// Format utilities
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export const formatNumber = (number) => {
  return new Intl.NumberFormat('nl-NL').format(number);
};

// Business logic utilities
export const identifyNiche = (campaignName) => {
  if (!campaignName) return 'Other';
  
  const campaign = campaignName.toLowerCase();
  
  if (campaign.includes('ontstoppen') || 
      campaign.includes('riool ontstoppen') || 
      campaign.includes('ontstoppingsdienst') || 
      campaign.includes('ontstoppingsbedrijf') ||
      campaign.includes('drain cleaning') ||
      campaign.includes('drain service') ||
      campaign.includes('afvoer') ||
      campaign.includes('verstopping')) {
    return 'Ontstoppen';
  }
  
  if (campaign.includes('loodgieter') ||
      campaign.includes('sanitair') ||
      campaign.includes('lekkage')) {
    return 'Loodgieter';
  }
  
  if (campaign.includes('dakdekker') ||
      campaign.includes('dak reparatie') ||
      campaign.includes('dakbedekking')) {
    return 'Dakdekker';
  }
  
  if (campaign.includes('schoorsteenveger') ||
      campaign.includes('schoorsteen') ||
      campaign.includes('rookkanaal')) {
    return 'Schoorsteenveger (NL)';
  }
  
  if (campaign.includes('birmingham') ||
      campaign.includes('plumber birmingham') ||
      campaign.includes('roofer birmingham')) {
    return 'UK Projects';
  }
  
  if (campaign.includes('drroof') ||
      campaign.includes('.com') ||
      campaign.includes('florida') ||
      campaign.includes('usa')) {
    return 'USA Projects';
  }
  
  if (campaign.includes('not set') || 
      campaign.trim() === '' ||
      campaign === 'undefined') {
    return 'Niet Ingesteld';
  }
  
  return 'Other';
};

export const extractCustomerCode = (campaignName) => {
  if (!campaignName) return null;
  const match = campaignName.match(CUSTOMER_CODE_PATTERN);
  return match ? match[1] : null;
};

export const getDefaultPrice = (niche) => {
  const defaultPrices = {
    'Ontstoppen': 32,
    'Loodgieter': 18,
    'Dakdekker': 42,
    'Schoorsteenveger (NL)': 25,
    'Other': 25
  };
  return defaultPrices[niche] || 25;
};

export const isRevenueGeneratingNiche = (niche) => {
  return ['Ontstoppen', 'Loodgieter', 'Dakdekker'].includes(niche);
};

export const calculateLeadEfficiency = (leadCalls, uniqueCalls) => {
  return uniqueCalls > 0 ? (leadCalls / uniqueCalls) * 100 : 0;
};

export const categorizePerformance = (totalTime) => {
  if (totalTime < 1000) return { category: 'EXCELLENT', color: 'green' };
  if (totalTime < 3000) return { category: 'GOOD', color: 'yellow' };
  return { category: 'NEEDS OPTIMIZATION', color: 'red' };
};

export const extractLocation = (campaignName) => {
  if (!campaignName) return 'Unknown';

  let cleanCampaign = campaignName;

  // Step 1: Remove "COOKIES:" prefix if present
  if (cleanCampaign.toLowerCase().startsWith('cookies:')) {
    cleanCampaign = cleanCampaign.replace(/^cookies:\s*/i, '');
  }

  // Step 2: Remove customer codes (pattern: space + code at end)
  cleanCampaign = cleanCampaign.replace(/\s+[A-Z]\d{3,4}[A-Za-z]?$/i, '');

  // Step 3: Remove known niche terms from the beginning
  const nicheKeywords = [
    'loodgieter', 'mr loodgieter', 'ontstoppen', 'riool ontstoppen', 'ontstoppingsdienst',
    'ontstoppingsbedrijf', 'drain cleaning', 'dakdekker', 'dak reparatie', 'dakbedekking',
    'schoorsteenveger', 'schoorsteen'
  ];

  // Sort by length (longest first) to match "riool ontstoppen" before "ontstoppen"
  const sortedNiches = nicheKeywords.sort((a, b) => b.length - a.length);
  for (const niche of sortedNiches) {
    const regex = new RegExp(`^${niche}\\s+`, 'i');
    if (regex.test(cleanCampaign)) {
      cleanCampaign = cleanCampaign.replace(regex, '');
      break;
    }
  }

  // Step 4: Extract remaining text as location
  const location = cleanCampaign.trim();

  // Step 5: Capitalize properly (handle Dutch articles and hyphenated names)
  if (location && location.length > 0) {
    return location
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Keep Dutch articles lowercase unless first word
        if (word === 'den') return 'Den';
        if (word === 'het') return 'Het';
        if (word === 'de') return 'De';
        // Handle hyphenated names (e.g., "Bergen-op-Zoom")
        if (word.includes('-')) {
          return word.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('-');
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  // Step 6: Fallback - try to find a known Dutch city in the original campaign
  const dutchCities = [
    'Amsterdam', 'Rotterdam', 'Den Haag', 'Utrecht', 'Eindhoven', 'Tilburg', 'Groningen', 'Almere',
    'Breda', 'Nijmegen', 'Enschede', 'Haarlem', 'Arnhem', 'Zaanstad', 'Amersfoort', 'Apeldoorn'
  ];
  const originalLower = campaignName.toLowerCase();
  for (const city of dutchCities) {
    if (originalLower.includes(city.toLowerCase())) {
      return city;
    }
  }

  return location || 'Other';
};
